import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import RTPageLayout from '../common/RTPageLayout';

const PAYMENT_TYPE_OPTIONS = [
  { value: '', label: 'All Payment Types' },
  { value: 'Payment', label: 'Payment' },
  { value: 'Adjustment', label: 'Adjustment' },
  { value: 'Advance', label: 'Advance' },
];

const PAYMENT_PROVIDER_OPTIONS = [
  { value: '', label: 'All Providers' },
  { value: 'Manual Entry', label: 'Manual Entry' },
  { value: 'Razorpay Gateway', label: 'Razorpay' },
  { value: 'PhonePe Gateway', label: 'PhonePe' },
];

const toApiDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getDefaultFilters = () => {
  const today = new Date();
  return {
    from_date: new Date(today.getFullYear(), today.getMonth(), 1),
    to_date: today,
    paymentType: '',
    paymentMethod: '',
    paymentProvider: '',
    occupancy_gender: '',
  };
};

const ViewPayments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [payments, setPayments] = useState([]);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAdjustments, setTotalAdjustments] = useState(0);
  const [totalNetAmount, setTotalNetAmount] = useState(0);

  const filterFields = useMemo(
    () => [
      {
        key: 'from_date',
        label: 'Start Date',
        type: 'date',
        width: 'compact',
        applyImmediately: true,
      },
      {
        key: 'to_date',
        label: 'End Date',
        type: 'date',
        width: 'compact',
        applyImmediately: true,
      },
      {
        key: 'paymentType',
        label: 'Payment Type',
        type: 'dropdown',
        width: 'medium',
        options: PAYMENT_TYPE_OPTIONS,
      },
      {
        key: 'paymentMethod',
        label: 'Payment Method',
        type: 'dropdown',
        width: 'medium',
        options: [
          { value: '', label: 'All Methods' },
          ...paymentMethods.map((method) => ({
            value: method.name,
            label: method.name,
          })),
        ],
      },
      {
        key: 'paymentProvider',
        label: 'Payment Provider',
        type: 'dropdown',
        width: 'medium',
        options: PAYMENT_PROVIDER_OPTIONS,
      },
      {
        key: 'occupancy_gender',
        label: 'Gender',
        type: 'dropdown',
        width: 'compact',
        options: OCCUPANCY_GENDER_FILTER_OPTIONS,
      },
    ],
    [paymentMethods],
  );

  useEffect(() => {
    const abortController = new AbortController();

    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadPaymentMethods(abortController),
          loadPaymentReport(appliedFilters, abortController, {
            isInitial: true,
          }),
        ]);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading initial data:', error);
        }
      } finally {
        setHasLoadedOnce(true);
        setLoading(false);
      }
    };

    loadInitialData();

    return () => {
      abortController.abort();
    };
  }, []);

  const loadPaymentMethods = async (abortController = null) => {
    try {
      const response = await apiGet('/payments/methods?status=active', {
        signal: abortController?.signal,
      });
      const data = response?.data || response || [];
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading payment methods:', error);
      }
    }
  };

  const loadPaymentReport = async (
    filterParams = appliedFilters,
    abortController = null,
    { isInitial = false } = {},
  ) => {
    try {
      if (!isInitial) setLoading(true);

      const params = new URLSearchParams();
      params.append('limit', '1000');

      const fromDate = toApiDate(filterParams.from_date);
      const toDate = toApiDate(filterParams.to_date);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      if (filterParams.paymentType) {
        params.append('payment_type', filterParams.paymentType);
      }

      if (filterParams.paymentMethod) {
        params.append('payment_method', filterParams.paymentMethod);
      }

      if (filterParams.paymentProvider) {
        params.append('payment_provider', filterParams.paymentProvider);
      }

      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }

      const response = await apiGet(
        `/payments/view-payments/?${params.toString()}`,
        {
          signal: abortController?.signal,
        },
      );

      const apiResponse = response.data;

      if (apiResponse && apiResponse.success && apiResponse.data) {
        setPayments(apiResponse.data);
        setTotalPayments(apiResponse.total || apiResponse.data.length);
        setTotalAmount(apiResponse.total_amount || 0);
        setTotalAdjustments(apiResponse.total_adjustments || 0);
        setTotalNetAmount(apiResponse.total_net_amount || 0);
      } else {
        toastError(apiResponse?.message || 'Failed to load payments');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading payments:', error);
        toastError('Error loading payments');
      }
    } finally {
      if (!isInitial) setLoading(false);
    }
  };

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = {
      ...filters,
      [key]:
        value instanceof Date || value === null || value === undefined
          ? value
          : (value ?? ''),
    };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      loadPaymentReport(next);
    }
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    loadPaymentReport(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.from_date) {
      pills.push({
        key: 'from_date',
        label: `Start: ${formatDateToDDMMYYYY(appliedFilters.from_date)}`,
      });
    }
    if (appliedFilters.to_date) {
      pills.push({
        key: 'to_date',
        label: `End: ${formatDateToDDMMYYYY(appliedFilters.to_date)}`,
      });
    }
    if (appliedFilters.paymentType) {
      const typeLabel =
        PAYMENT_TYPE_OPTIONS.find((o) => o.value === appliedFilters.paymentType)
          ?.label || appliedFilters.paymentType;
      pills.push({ key: 'paymentType', label: `Payment Type: ${typeLabel}` });
    }
    if (appliedFilters.paymentMethod) {
      pills.push({
        key: 'paymentMethod',
        label: `Payment Method: ${appliedFilters.paymentMethod}`,
      });
    }
    if (appliedFilters.paymentProvider) {
      const providerLabel =
        PAYMENT_PROVIDER_OPTIONS.find(
          (o) => o.value === appliedFilters.paymentProvider,
        )?.label || appliedFilters.paymentProvider;
      pills.push({
        key: 'paymentProvider',
        label: `Payment Provider: ${providerLabel}`,
      });
    }
    if (appliedFilters.occupancy_gender) {
      const genderLabel =
        OCCUPANCY_GENDER_FILTER_OPTIONS.find(
          (o) => o.value === appliedFilters.occupancy_gender,
        )?.label || appliedFilters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const defaultFilters = getDefaultFilters();
    const next = { ...appliedFilters };
    if (key === 'from_date') {
      next.from_date = defaultFilters.from_date;
    } else if (key === 'to_date') {
      next.to_date = defaultFilters.to_date;
    } else {
      next[key] = '';
    }
    setFilters(next);
    setAppliedFilters(next);
    loadPaymentReport(next);
  };

  const getTableTitle = () => {
    let title = 'Payments';
    if (appliedFilters.from_date && appliedFilters.to_date) {
      title += ` - ${formatDateToDDMMYYYY(appliedFilters.from_date)} to ${formatDateToDDMMYYYY(appliedFilters.to_date)}`;
    }
    title += ` (${totalPayments} record${totalPayments !== 1 ? 's' : ''})`;
    return title;
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const columns = [
    {
      Header: 'Payment Date',
      accessor: 'payment_date',
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) =>
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (
          value || 'N/A'
        ),
    },
    {
      Header: 'Invoice Items',
      accessor: 'items_text',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Payment Type',
      accessor: 'payment_type_name',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ value }) => formatAmount(value),
    },
    {
      Header: 'Payment Method',
      accessor: 'payment_method',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Payment Provider',
      accessor: 'payment_provider',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton
          type="link"
          size="sm"
          onClick={() => navigate(`/payments/${row.id}/view`)}
        >
          View Payment
        </RTButton>
      ),
    },
  ];

  return (
    <RTPageLayout activePath={location.pathname} title="View Payments">
      {!hasLoadedOnce ? (
        <div
          className="rt-main-section"
          style={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RTSpinner message="Loading payments..." fullScreen={false} />
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '0.75rem',
              marginBottom: '0.75rem',
            }}
          >
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #153C7F',
                borderRight: '4px solid #153C7F',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Total Payments
              </div>
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: '#153C7F',
                }}
              >
                {totalPayments}
              </div>
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #153C7F',
                borderRight: '4px solid #153C7F',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Total Amount
              </div>
              <div
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#153C7F',
                }}
              >
                {formatAmount(totalAmount)}
              </div>
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #153C7F',
                borderRight: '4px solid #153C7F',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Total Adjustments
              </div>
              <div
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#153C7F',
                }}
              >
                {formatAmount(totalAdjustments)}
              </div>
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #153C7F',
                borderRight: '4px solid #153C7F',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Amount After Adjustments
              </div>
              <div
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#153C7F',
                }}
              >
                {formatAmount(totalNetAmount)}
              </div>
            </div>
          </div>

          <div className="rt-main-section">
            <RTFilteredTable
              loading={loading}
              loadingMessage="Loading payments..."
              filters={
                <RTFilters
                  fields={filterFields}
                  values={filters}
                  onChange={handleFilterChange}
                  pills={appliedFilterPills}
                  onRemovePill={handleRemoveAppliedFilter}
                  onClearAll={handleClearFilters}
                />
              }
            >
              <Table
                columns={columns}
                data={payments}
                title={getTableTitle()}
                showSearch={true}
                exportable={true}
                exportFileName="payments"
                showPagination={true}
                noDataText="No payments found for the selected criteria"
              />
            </RTFilteredTable>
          </div>
        </>
      )}
    </RTPageLayout>
  );
};

export default ViewPayments;
